// deployment_class: customer_env_allowed
/**
 * The template's server contract — what replaced `localStorage`.
 *
 * Mirrors `DashboardTemplate` in `src/v3/templates/types.ts`, which the backend
 * assembles from four tables. The two trees are separate TypeScript projects
 * with no shared package, so this is a hand-kept mirror like
 * `board/api.ts` — and like that one, it renders what the server said and
 * derives nothing of its own.
 *
 * ── Why the stored shape and the wire shape are different types ────────────
 * `template-state.ts` models a template as `{ name, modules }` — SHAPE only,
 * which is what the editor manipulates. The server additionally returns
 * identity: `id`, `organizationId`, `version`. That identity is what makes
 * saving possible at all (there is a row to write back to) and what makes a
 * STOCK template recognisable (`organizationId === null`), so it is carried
 * here rather than discarded on arrival.
 */
import { apiFetch } from '@v3-shell/api/api-client-base';
import type { DashboardTemplate, ModuleTemplate } from './template-state';

/** A template as the server returns it — the editor's shape plus identity. */
export interface ServerTemplate {
  id: string;
  /** `null` = STOCK: ships with Kathedral, owned by no customer, read-only. */
  organizationId: string | null;
  name: string;
  version: number;
  isActive: boolean;
  modules: ModuleTemplate[];
}

/** A row in the template picker — enough to list without loading the shape. */
export interface TemplateSummary {
  id: string;
  organizationId: string | null;
  name: string;
  version: number;
  isStock: boolean;
}

/** The editor's view of a server template. Identity is held separately. */
export function toEditable(t: ServerTemplate): DashboardTemplate {
  return { name: t.name, modules: t.modules };
}

/** A stock template is the product's, not the customer's — editing copies it. */
export function isStockTemplate(t: Pick<ServerTemplate, 'organizationId'>): boolean {
  return t.organizationId === null;
}

/** The body the write endpoints accept — shape only; the server owns identity. */
function draftOf(t: DashboardTemplate): { name: string; modules: readonly ModuleTemplate[] } {
  return { name: t.name, modules: t.modules };
}

/**
 * The template this user's dashboard renders when they have chosen nothing.
 *
 * A blank environment answers with the STOCK template seeded by migration 0174,
 * so this has a result before a customer has authored anything — which is the
 * state every environment is provisioned in.
 */
export function fetchDefaultTemplate(): Promise<ServerTemplate> {
  return apiFetch<ServerTemplate>('/v3/templates/default');
}

export async function listTemplates(): Promise<TemplateSummary[]> {
  const { templates } = await apiFetch<{ templates: TemplateSummary[] }>('/v3/templates');
  return templates;
}

export function fetchTemplate(id: string): Promise<ServerTemplate> {
  return apiFetch<ServerTemplate>(`/v3/templates/${encodeURIComponent(id)}`);
}

export function createTemplate(t: DashboardTemplate): Promise<ServerTemplate> {
  return apiFetch<ServerTemplate>('/v3/templates', {
    method: 'POST',
    body: JSON.stringify(draftOf(t)),
  });
}

export function updateTemplate(id: string, t: DashboardTemplate): Promise<ServerTemplate> {
  return apiFetch<ServerTemplate>(`/v3/templates/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(draftOf(t)),
  });
}

/**
 * Copy a readable template into one this organisation owns.
 *
 * This is what "saving" a stock template does. Separate from update because it
 * CREATES: conflating them would make an edit silently produce a new row, which
 * ends with two templates and no idea which one is live.
 */
export function copyTemplate(id: string, name: string): Promise<ServerTemplate> {
  return apiFetch<ServerTemplate>(`/v3/templates/${encodeURIComponent(id)}/copy`, {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export function deleteTemplate(id: string): Promise<void> {
  return apiFetch<void>(`/v3/templates/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
